/**
 * Verdict trend chart — thin wrapper around TimeSeriesChart.
 *
 * @module components/VerdictChart
 */

import React, { useMemo }  from 'react';
import TimeSeriesChart      from './TimeSeriesChart';
import * as constants       from '../constants';
import { useI18n }          from '../dataProviders/I18nProvider';

/**
 * Transforms raw verdict rows into a recharts-friendly array.
 * Each element represents one time bucket with a key per decision type.
 *
 * @param {object[]} rows - Array of { bucket, decision, count }.
 * @returns {object[]} Recharts data array.
 */
function transformData(rows) {
  const bucketMap = new Map();

  for (const row of rows) {
    const key = row.bucket;
    if (!bucketMap.has(key)) {
      const entry = { bucket: key, time: new Date(key).getTime() };
      for (const dt of constants.DECISION_TYPES) entry[dt] = 0;
      bucketMap.set(key, entry);
    }
    bucketMap.get(key)[row.decision] = row.count;
  }

  return Array.from(bucketMap.values());
}

/**
 * Renders a single verdict trend chart for one API key.
 *
 * @param {object}   props
 * @param {object[]} props.data              - Array of { bucket, decision, count }.
 * @param {string}   props.apiKeyDescription - Description of the API key.
 * @param {string}   props.interval          - Time interval (hour/day/week/month).
 * @param {number}   props.rangeStart        - Epoch ms for x-axis domain start.
 * @param {number}   props.rangeEnd          - Epoch ms for x-axis domain end.
 * @param {boolean}  props.useLocalTime      - Whether to display in browser local time.
 * @returns {React.ReactElement}
 */
export default function VerdictChart({ data, apiKeyDescription, interval,
                                       rangeStart, rangeEnd, useLocalTime }) {
  const { t }     = useI18n();
  const chartData = useMemo(() => transformData(data), [data]);

  return (
    <TimeSeriesChart
      data={chartData}
      title={t('dashboard.chartTitle', { description: apiKeyDescription })}
      series={constants.DECISION_TYPES}
      colors={constants.DECISION_COLORS}
      legendFormatter={(value) => t(`dashboard.decision.${value}`)}
      interval={interval}
      rangeStart={rangeStart}
      rangeEnd={rangeEnd}
      useLocalTime={useLocalTime}
    />
  );
}

export { transformData };
